import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { Button, Heading } from '@ui/themed';
import { t } from 'i18next';
import {
  cloneElement,
  Fragment,
  isValidElement,
  type MouseEventHandler,
  type ReactElement,
} from 'react';
import { PageModalProvider, usePageModal } from './context';
import type { PageModalContentProps, PageModalProps, PageModalTriggerProps } from './types';

export { usePageModal } from './context';

export function PageModal(props: PageModalProps) {
  return <PageModalProvider>{props.children}</PageModalProvider>;
}

function PageModalTrigger(props: PageModalTriggerProps) {
  const { open } = usePageModal();

  if (props.asChild) {
    if (!isValidElement(props.children)) {
      throw new Error('<PageModal.Trigger asChild> expects a single React element as its child');
    }
    const child = props.children as ReactElement<{ onClick?: MouseEventHandler<HTMLElement> }>;
    return cloneElement(child, {
      onClick: (event: React.MouseEvent<HTMLElement>) => {
        child.props.onClick?.(event);
        if (!event.defaultPrevented) {
          open();
        }
      },
    });
  }

  return (
    <Button variant="primary" onClick={open}>
      {props.children as React.ReactNode}
    </Button>
  );
}
PageModalTrigger.displayName = 'PageModal.Trigger';

function PageModalContent(props: PageModalContentProps) {
  const { isOpen, close } = usePageModal();

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog className="relative z-50" onClose={close}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="relative flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow">
                <div className="sticky top-0 z-10 flex shrink-0 items-center justify-between gap-4 border-gray-200 border-b bg-white px-5 py-4">
                  <DialogTitle as={Fragment}>
                    <Heading level="h1" style="xl">
                      {props.title}
                    </Heading>
                  </DialogTitle>
                  <Button onClick={close} ariaLabel={t('aria:close')} icon="x" variant="bare" />
                </div>
                <div className="flex-1 overflow-y-auto px-5 py-5">{props.children}</div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
PageModalContent.displayName = 'PageModal.Content';

PageModal.Trigger = PageModalTrigger;
PageModal.Content = PageModalContent;
